import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CloseCircleOutlined, ReloadOutlined, HomeOutlined, PhoneOutlined, LoadingOutlined } from '@ant-design/icons';
import { createPayment, getPayment, type CreatePaymentInput } from '../../api/payment';
import { useToastContext } from '../../context/ToastContext';
import { ROUTES } from '@utils/constants';
import './styles/failure.css';
import { GetPaymentOutput } from '@dev-zeromoblt/o-platform-client';

export function PaymentFailure() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const txnid = searchParams.get('txnid');
  const error = searchParams.get('error');
  const { addToast } = useToastContext();

  const [payment, setPayment] = useState<GetPaymentOutput | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [retryBlocked, setRetryBlocked] = useState(false);
  const supportNumber = import.meta.env.VITE_PARENT_SUPPORT_NUMBER;

  const MAX_RETRIES = 3;
  const RETRY_WINDOW_MS = 60 * 60 * 1000; // 1 hour

  // Load retry state from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('paymentRetryState');
      if (stored) {
        const state = JSON.parse(stored);
        const elapsed = Date.now() - (state.firstRetryAt || 0);
        if (elapsed < RETRY_WINDOW_MS && state.count >= MAX_RETRIES) {
          setRetryCount(state.count);
          setRetryBlocked(true);
        } else if (elapsed >= RETRY_WINDOW_MS) {
          // Window expired, reset
          sessionStorage.removeItem('paymentRetryState');
          setRetryCount(0);
          setRetryBlocked(false);
        } else {
          setRetryCount(state.count || 0);
        }
      }
    } catch {
      // ignore parse errors
    }
  }, []);

  const handleContactSupport = () => {
    if (!supportNumber) {
      addToast({
        type: 'error',
        message: 'Unable to contact support right now. Please try again later.',
      });
      return;
    }
    window.location.href = `tel:${supportNumber}`;
  };

  useEffect(() => {    
    if (!txnid) {
      console.error('[PaymentFailure] No transaction ID provided, redirecting to welcome');
      navigate(ROUTES.WELCOME);
      return;
    }
    
    getPayment(txnid)
      .then((paymentData) => {
        setPayment(paymentData);
      })
      .catch((error) => {
        console.error('[PaymentFailure] Error fetching payment details:', {
          error: error.message,
          txnid,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [txnid, navigate]);

  const handleRetry = async () => {
    // Check retry limits
    const stored = sessionStorage.getItem('paymentRetryState');
    let state = stored ? JSON.parse(stored) : { count: 0, firstRetryAt: 0 };
    const elapsed = Date.now() - (state.firstRetryAt || 0);

    // Reset window if expired
    if (elapsed >= RETRY_WINDOW_MS) {
      state = { count: 0, firstRetryAt: 0 };
    }

    if (state.count >= MAX_RETRIES) {
      setRetryBlocked(true);
      addToast({
        message: `Maximum ${MAX_RETRIES} retry attempts reached. Please try again later or contact support.`,
        type: 'error',
      });
      return;
    }

    // Update retry count
    const newCount = state.count + 1;
    const firstRetryAt = state.firstRetryAt || Date.now();
    sessionStorage.setItem('paymentRetryState', JSON.stringify({ count: newCount, firstRetryAt }));
    setRetryCount(newCount);

    if (newCount >= MAX_RETRIES) {
      setRetryBlocked(true);
    }

    setIsRetrying(true);

    try {
      // Use the failed payment record as the source of truth.
      // payment.amount already has the correct adjusted amount (base ± transaction adjustments)
      // as set by UserDetails when the payment was originally created.
      const isRenewal = !!(payment as any)?.subscriptionV2Id;

      const paymentPayload: CreatePaymentInput = {
        subscriberId: payment?.subscriberId!,
        granteeId: payment?.granteeId!,
        email: payment?.email!,
        phone: payment?.phone!,
        amount: payment?.amount!,
        productInfo: payment?.productInfo || '',
        paymentPlanId: payment?.paymentPlanId,
        tags: payment?.tags,
        ...(isRenewal && {
          subscriptionV2Id: (payment as any).subscriptionV2Id,
          subscriptionV2InstallmentNumber: (payment as any).subscriptionV2InstallmentNumber,
        }),
        ...(!isRenewal && {
          subscriptionStartDate: (payment as any)?.subscriptionStartDate,
        }),
        ...(() => {
          const ids = ((payment as any)?.transactionIds as string[] | undefined)?.filter(Boolean) ?? [];
          if (ids.length > 0) return { transactionIds: ids };
          if ((payment as any)?.transactionId) return { transactionId: (payment as any).transactionId };
          return {};
        })(),
      };

      const paymentData = await createPayment(paymentPayload);

      // Store new payment ID for tracking
      localStorage.setItem('currentPaymentId', paymentData.id);

      // Navigate to payment processing with the new payment data
      navigate(ROUTES.PAYMENT_PROCESSING, {
        state: { paymentResponse: paymentData },
      });

    } catch (error: any) {
      console.error('[PaymentFailure] Error creating retry payment:', {
        error: error.message,
        stack: error.stack,
        response: error.response?.data,
      });
      addToast({
        message: error.message || 'Failed to initiate payment. Please try again.',
        type: 'error',
      });
    } finally {
      setIsRetrying(false);
    }
  };

  if (loading) {
    return <div className="payment-failure__loading">Loading payment details...</div>;
  }

  return (
    <div className="payment-failure">
      <div className="payment-failure__container">
        <div className="payment-failure__icon">
          <div className="payment-failure__icon-wrapper">
            <CloseCircleOutlined className="payment-failure__x" style={{ fontSize: 40 }} />
          </div>
        </div>

        <h1 className="payment-failure__title">Payment Failed</h1>
        <p className="payment-failure__subtitle">
          {error || 'Your payment could not be processed'}
        </p>

        <div className="payment-failure__card">
          <div className="payment-failure__detail">
            <span className="payment-failure__label">Transaction ID</span>
            <span className="payment-failure__value">{payment?.id || txnid}</span>
          </div>
          <div className="payment-failure__detail">
            <span className="payment-failure__label">Amount</span>
            <span className="payment-failure__value">₹{payment?.amount || '0'}</span>
          </div>
          <div className="payment-failure__detail">
            <span className="payment-failure__label">Status</span>
            <span className="payment-failure__value payment-failure__value--failed">
              Failed
            </span>
          </div>
        </div>

        <div className="payment-failure__info">
          <p>Don't worry! Your payment plan is saved. You have {MAX_RETRIES} retry attempts.</p>
        </div>

        <div className="payment-failure__actions">
          <button
            onClick={handleRetry}
            disabled={isRetrying || retryBlocked}
            className="payment-failure__button payment-failure__button--primary"
          >
            {isRetrying ? (
              <>
                <LoadingOutlined />
                Processing...
              </>
            ) : retryBlocked ? (
              'Retry limit reached'
            ) : (
              <>
                <ReloadOutlined />
                Retry Payment ({MAX_RETRIES - retryCount} left)
              </>
            )}
          </button>
          <button
            onClick={() => !isRetrying && navigate(ROUTES.WELCOME)}
            disabled={isRetrying}
            className="payment-failure__button payment-failure__button--secondary"
          >
            <HomeOutlined /> 
            Go to Dashboard
          </button>
        </div>

        <div className="payment-failure__actions--single">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleContactSupport();
            }}
            className="payment-failure__button payment-failure__button--support"
            rel="noreferrer"
          >
            <PhoneOutlined /> Contact Support
          </a>
        </div>

        {/* Loading Modal Overlay */}
        {isRetrying && (
          <div className="payment-failure__modal-overlay">
            <div className="payment-failure__modal">
              <LoadingOutlined spin style={{ fontSize: 48, color: '#1890ff' }} />
              <h3>Processing Payment</h3>
              <p>Please wait while we initiate your payment...</p>
            </div>
          </div>
        )}

        <div className="payment-failure__help">
          <h3>Common Issues</h3>
          <ul>
            <li>Insufficient funds in account</li>
            <li>Bank server temporarily down</li>
            <li>Card limit exceeded</li>
            <li>Incorrect payment details</li>
          </ul>
          <p>If the issue persists, please contact support.</p>
        </div>
      </div>
    </div>
  );
}